/**
 * Глобальный фильтр периода (date_from / date_to) + регион — общий для
 * всех страниц. Хранится в URL search params, переживает reload.
 *
 * Использование:
 *   const { from, to, region } = usePeriod();
 *   useQuery({ queryKey: ["overview", from, to, region], queryFn: () => fetchOverview({date_from: from, date_to: to, region}) });
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type PeriodPreset = "today" | "week" | "month" | "quarter" | "year" | "ytd" | "custom";

export type Region = "all" | "Москва" | "Санкт-Петербург";

interface PeriodCtx {
  from: string;       // YYYY-MM-DD
  to: string;         // YYYY-MM-DD
  preset: PeriodPreset;
  setPreset: (p: PeriodPreset) => void;
  setRange: (from: string, to: string) => void;
  label: string;
  region: Region;
  setRegion: (r: Region) => void;
}

const PeriodContext = createContext<PeriodCtx | null>(null);

function todayMSK(): Date {
  // МСК = UTC+3
  const now = new Date();
  return new Date(now.getTime() + 3 * 60 * 60 * 1000);
}

function fmtIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function presetRange(p: PeriodPreset): [string, string] {
  const today = todayMSK();
  const todayStr = fmtIso(today);
  if (p === "today") return [todayStr, todayStr];
  if (p === "week") {
    const weekAgo = new Date(today.getTime() - 6 * 86400 * 1000);
    return [fmtIso(weekAgo), todayStr];
  }
  if (p === "month") {
    const monthAgo = new Date(today.getTime() - 29 * 86400 * 1000);
    return [fmtIso(monthAgo), todayStr];
  }
  if (p === "quarter") {
    const qAgo = new Date(today.getTime() - 89 * 86400 * 1000);
    return [fmtIso(qAgo), todayStr];
  }
  if (p === "year") {
    const yAgo = new Date(today.getTime() - 364 * 86400 * 1000);
    return [fmtIso(yAgo), todayStr];
  }
  if (p === "ytd") {
    const yStart = new Date(Date.UTC(today.getUTCFullYear(), 0, 1));
    return [fmtIso(yStart), todayStr];
  }
  return [todayStr, todayStr];
}

function readUrl(): { from: string; to: string; preset: PeriodPreset; region: Region } {
  const params = new URLSearchParams(window.location.search);
  const from = params.get("from") || "";
  const to = params.get("to") || "";
  const preset = (params.get("preset") as PeriodPreset) || "week";
  const regionParam = params.get("region");
  const region: Region = (regionParam === "Москва" || regionParam === "Санкт-Петербург") ? regionParam : "all";
  if (preset === "custom" && from && to) {
    return { from, to, preset, region };
  }
  const [pf, pt] = presetRange(preset === "custom" ? "week" : preset);
  return { from: pf, to: pt, preset: preset === "custom" ? "week" : preset, region };
}

function writeUrl(from: string, to: string, preset: PeriodPreset, region: Region) {
  const params = new URLSearchParams(window.location.search);
  params.set("preset", preset);
  if (preset === "custom") {
    params.set("from", from);
    params.set("to", to);
  } else {
    params.delete("from");
    params.delete("to");
  }
  if (region !== "all") params.set("region", region);
  else params.delete("region");
  const newUrl = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
  window.history.replaceState({}, "", newUrl);
}

export function PeriodProvider({ children }: { children: ReactNode }) {
  const initial = readUrl();
  const [preset, setPresetState] = useState<PeriodPreset>(initial.preset);
  const [from, setFrom] = useState<string>(initial.from);
  const [to, setTo] = useState<string>(initial.to);
  const [region, setRegionState] = useState<Region>(initial.region);

  const setPreset = useCallback((p: PeriodPreset) => {
    setPresetState(p);
    if (p !== "custom") {
      const [f, t] = presetRange(p);
      setFrom(f);
      setTo(t);
      writeUrl(f, t, p, region);
    }
  }, [region]);

  const setRange = useCallback((f: string, t: string) => {
    setPresetState("custom");
    setFrom(f);
    setTo(t);
    writeUrl(f, t, "custom", region);
  }, [region]);

  const setRegion = useCallback((r: Region) => {
    setRegionState(r);
    writeUrl(from, to, preset, r);
  }, [from, to, preset]);

  // Обновляем URL при первом монтировании если пустой
  useEffect(() => { writeUrl(from, to, preset, region); /* eslint-disable-next-line */ }, []);

  const label = useMemo(() => {
    const map: Record<PeriodPreset, string> = {
      today: "Сегодня",
      week: "Эта неделя",
      month: "Месяц",
      quarter: "Квартал",
      year: "Год",
      ytd: "С начала года",
      custom: "—",
    };
    if (preset !== "custom") return map[preset];
    const f = from.slice(8, 10) + "." + from.slice(5, 7) + "." + from.slice(2, 4);
    const t = to.slice(8, 10) + "." + to.slice(5, 7) + "." + to.slice(2, 4);
    return `${f} — ${t}`;
  }, [preset, from, to]);

  return (
    <PeriodContext.Provider value={{ from, to, preset, setPreset, setRange, label, region, setRegion }}>
      {children}
    </PeriodContext.Provider>
  );
}

export function usePeriod(): PeriodCtx {
  const ctx = useContext(PeriodContext);
  if (!ctx) throw new Error("usePeriod must be used within PeriodProvider");
  return ctx;
}
